import { IconButton, Tab, Tabs } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles, styled, withStyles } from "@material-ui/styles";
import { useLinked, useModel } from "@type-r/react";
import { InblockIcon } from "common/invoice-aging/iconography";
import { Button, CollapsableSection, Column, Flex, asTimelessDateUTC, div } from "lqx-react";
import React, { useEffect } from "react";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { Can, hasRole } from "src/app/services/can";
import { Invoice, Monetization } from "../api/invoice";
import { InvoiceEditDTO } from "../api/invoiceDTO";
import { HeaderTitle } from "../components/asset-details-pane-styles";
import { CopyRow } from "../components/copy-row";
import { ActivityLog } from "./invoice-activity-logs-tab";
import { EditInvoiceTab } from "./invoice-edit-tab";
import { InvoiceSendToTab } from "./invoice-send-to-tab";
import { handleDate } from "lqx-react/src/formatters";

export const InvoiceDetailsPane = ({ invoice, close, invoiceId, $selectedTab, invoiceData, isArchived }: { invoice: Invoice, close, invoiceId, $selectedTab, invoiceData?, isArchived?}) => {
    const classes = useInvoiceDetailsPaneStyles();
    const InblockedIconClasses = useInblockedIconStyles();
    const assetUid = invoice.uid;
    const invoiceEdit = useModel(Invoice);
    const invoiceEditDto = useModel(InvoiceEditDTO);
    const validationData = useLinked({});
    const isEdit = $selectedTab.value === 'edit';

    useEffect(() => {
        validationData.set({});
        invoiceEdit.set(invoice.attributes);
    }, [$selectedTab.value, invoiceId]);

    const save = async () => {
        const upDate = () => {
            invoice.assignFrom(invoiceEdit);
            $selectedTab.set('details')
        }
        await invoiceEditDto.update(invoiceId, { ...invoiceData.attributes, dataset: invoiceEdit }, upDate, validationData);
    }

    const editOpen = async () => {
        $selectedTab.set("edit")
    }

    const cancelEdit = () => {
        $selectedTab.set("details")
    }

    return <Container>
        <Header>
            <IconButton aria-label="Close" size="small" className={classes.iconButton} onClick={() => close()}>
                <CloseIcon />
            </IconButton>
            <Flex alignCenter>
                {invoice.isDigitized ? <>
                    <InblockIcon src="/assets/images/inblock_logo.png" classes={InblockedIconClasses} />
                    <StatusWithoutMargin>{invoice.state.name}</StatusWithoutMargin> </>
                    : <Status>{invoice.state.name}</Status>}
            </Flex>
            <HeaderTitle><>Invoice {invoice.number || 'N/A'}</></HeaderTitle>
        </Header>
        {Can(UserEntitlementCatalogs.EditVaultInvoicesPage) && (
            <ButtonsBlock>
                <Flex row justifyCenter>
                    {!isEdit && <EditButton onClick={editOpen} color="primary">Edit</EditButton>}
                    {isEdit && <EditButton onClick={save} color="primary">Save</EditButton>}
                    {isEdit && <EditButton onClick={cancelEdit} color="primary">Cancel</EditButton>}
                </Flex>
            </ButtonsBlock>
        )}
        <InvoiceTabs value={$selectedTab.value}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(evt, newValue) => $selectedTab.set(newValue)} >
            <InvoiceTab value="details" label=" Details" disabled={isEdit} />
            <InvoiceTab value="activityLog" label="Activity Log" disabled={isEdit} />
            {invoice.isDigitized && <InvoiceTab value="sendTo" label=" Send To" disabled={isEdit || isArchived} />}
        </InvoiceTabs>
        <InvoiceTabContent>
            {$selectedTab.value === 'details' && <Details invoice={invoice} invoiceId={invoiceId} />}
            {$selectedTab.value === 'edit' && <EditInvoiceTab invoice={invoiceEdit} errors={validationData.value} />}
            {$selectedTab.value === 'activityLog' && <ActivityLog assetId={invoiceId} />}
            {$selectedTab.value === 'sendTo' && <InvoiceSendToTab assetId={invoiceId} assetUid={assetUid} />}
        </InvoiceTabContent>
    </Container>
};

const Details = ({ invoice, invoiceId }: { invoice: Invoice, invoiceId }) => {
    const sectionStyles = SectionStyles();
    const applyPercentageFormat = (num: number) => (num ? num : 0).toLocaleString('en-US', { minimumFractionDigits: 10, maximumFractionDigits: 10 }) + '%';
    const monetization = invoice.proposedMonetization || new Monetization();

    return (<>
        <CollapsableSection title="General" titleClassName={sectionStyles.sectionTitle} noBorder noShadow>
            <Column>
                <CopyRow label="Invoice Number" value={invoice.number} />
                <CopyRow label="Buyer Name" value={invoice.buyer.name} />
                <CopyRow label="Supplier Name" value={invoice.supplier.name} />
                <CopyRow label="Governing Law" value={invoice.governingLaw ? invoice.governingLaw.name : ''} />
                <CopyRow label="Face Value" currency value={invoice.faceValue} />
                <CopyRow label="Currency" value={invoice.currency} />
                <CopyRow label="Invoice Date" date={!!invoice.invoiceDate} value={handleDate(invoice.invoiceDate)} />
                <CopyRow label="Invoice Due Date" date={!!invoice.invoiceDueDate} value={handleDate(invoice.invoiceDueDate)} />
                <CopyRow label="Funding Date" date={!!invoice.fundingDate} value={handleDate(invoice.fundingDate)} />
                <CopyRow label="Anticipated Payment Date" date={!!invoice.anticipatedPaymentDate} value={handleDate(invoice.anticipatedPaymentDate)} />
                <CopyRow label="Anticipated Settlement Date" date={!!invoice.anticipatedSettlementDate} value={handleDate(invoice.anticipatedSettlementDate)} />
                <CopyRow label="Effective Date" date={!!invoice.effectiveDate} value={handleDate(invoice.effectiveDate)} />
                <CopyRow label="Promise To Pay Date" date={!!invoice.promisePayDate} value={handleDate(invoice.promisePayDate)} />
                <CopyRow label="Adjustment Amount" currency value={invoice.adjustmentAmount} />
                <CopyRow label="Outstanding Amount Seller" currency value={invoice.outstandingAmountSeller} />
                <CopyRow label="Adjustment Face Value" currency value={invoice.adjustedFaceValue} />

                <CopyRow label="Payment Terms" value={invoice.paymentTerms} />
                <CopyRow label="Purchase Order Number" number value={invoice.purchaseOrder.number} />
                <CopyRow label="Asset ID" value={invoiceId} />
                <CopyRow label="Asset UID" value={invoice.uid} />
                <CopyRow label="Import Job ID" value={invoice.importJobId} hyperlinkHref={`/import-jobs/?importJobId=${invoice.importJobId}`} />
                <CopyRow label="Created Date" date={!!invoice.createdOn} value={handleDate(invoice.createdOn)} />
                <CopyRow label="Updated Date" date={!!invoice.updatedOn} value={handleDate(invoice.updatedOn)} />
                <CopyRow label="Alternate ID" number value={invoice.alternateId} />
                <CopyRow label="Notification Number" number value={invoice.notificationNumber} />
                <CopyRow label="Ref 1" number value={invoice.ref1} />
                <CopyRow label="Ref 2" number value={invoice.ref2} />
                <CopyRow label="Ref 3" number value={invoice.ref3} />
            </Column>
        </CollapsableSection>
        <CollapsableSection title="Monetization Fields" titleClassName={sectionStyles.sectionTitle} noBorder noShadow>
            <Column>
                {!hasRole('AssetFacilitator') && <CopyRow label="Funder Name" value={monetization.funder ? monetization.funder.name : ''} />}
                <CopyRow label="Net Face Value" currency value={monetization.netFaceValue} />
                <CopyRow label="Tenor" value={monetization.tenor} />
                <CopyRow label="Base Rate" value={monetization.baseRate} />
                <CopyRow label="Base Rate (%)" value={applyPercentageFormat(monetization.baseRatePercentage)} />
                <CopyRow label="Base Rate Amount" currency value={monetization.baseRateAmount} />
                <CopyRow label="Margin Rate" value={monetization.marginRate} />
                <CopyRow label="Margin Rate (%)" value={applyPercentageFormat(monetization.marginRatePercentage)} />
                <CopyRow label="Margin Rate Amount" currency value={monetization.marginRateAmount} />
                <CopyRow label="Total Rate" value={monetization.totalRate} />
                <CopyRow label="Total Rate (%)" value={applyPercentageFormat(monetization.totalRatePercentage)} />
                <CopyRow label="Total Rate Amount" currency value={monetization.totalRateAmount} />
                <CopyRow label="Purchase Price" currency value={monetization.purchasePrice} />
            </Column>
        </CollapsableSection>
    </>);
}

const Container = div({
    height: '100%',
    backgroundColor: 'white',
    padding: '0px',
    marginTop: '26px',
});

const Header = div({
    display: 'contents',
    width: '100%',
    height: '100%',
    border: '1px solid #E2F0F7',
    paddingTop: 0
});

const Status = div({
    color: '#63a9dc',
    height: '15px',
    marginLeft: '21px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '15px',
});

const StatusWithoutMargin = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '15px',
});

const InvoiceTabs = withStyles({
    indicator: {
        display: 'none'
    },
    root: {
        marginTop: '8px'
    }
})((props: any) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

const InvoiceTab = withStyles({
    root: {
        minWidth: '73px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '3px 10px 6px',
        position: 'static',
        height: '26px',
        left: '46.58px',
        background: (props: any) => props.selected ? '#DCEEFD' : 'white',
        fontWeight: (props: any) => props.selected ? 600 : 400,
        borderRadius: '4px',
        flex: 'none',
        margin: '0px 20px',
        textTransform: 'none'
    },
})((props: any) => <Tab disableRipple {...props} />);

const InvoiceTabContent = div({
    border: '1px solid #E2F0F7',
    marginTop: '10px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '15px',
    overflow: 'auto',
    height: 'calc(100vh - 410px)'
});

const useInblockedIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '15px',
        marginRight: '2px',
        marginLeft: '21px',
    }
});

const SectionStyles = makeStyles({
    sectionTitle: {
        color: '#63a9dc',
        fontSize: '13px',
        marginRight: '1em',
        display: 'flex',
        alignItems: 'center'
    }
})

const useInvoiceDetailsPaneStyles = makeStyles({
    iconButton: {
        display: 'block',
        marginLeft: 'auto',
        marginTop: '10px',
        marginRight: '10px',
    }
})

const EditButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});

export const ButtonsBlock = div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '6px'
});